import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Search, MapPin, Check, Scale } from "lucide-react";

/** Arrange mode — phone-friendly rack-up. Pick the rack you're filling, search a
 * can, tap "Put here". Optionally correct the weight while the can is on the scale. */
export default function Arrange() {
  const qc = useQueryClient();
  const { data: containers = [] } = useQuery({
    queryKey: ["containers-list"],
    queryFn: async () => (await api.get("/containers")).data,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["items-all"],
    queryFn: async () => (await api.get("/items", { params: { include_inactive: true } })).data,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data.filter(l => l.active),
  });
  const [rack, setRack] = useState("");
  const [q, setQ] = useState("");
  const [placed, setPlaced] = useState(0);
  const [hideDone, setHideDone] = useState(true);

  const itemById = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items]);
  const locById = useMemo(() => Object.fromEntries(locations.map(l => [l.id, l])), [locations]);
  const locPath = (id) => {
    const parts = []; let cur = locById[id]; const seen = new Set();
    while (cur && !seen.has(cur.id)) { parts.unshift(cur.name); seen.add(cur.id); cur = locById[cur.parent_id]; }
    return parts.join(" › ");
  };

  const query = q.trim().toLowerCase();
  const rows = containers
    .map(c => ({ ...c, item: itemById[c.item_id] }))
    .filter(c => c.item)
    .filter(c => !hideDone || !rack || c.location_id !== rack)
    .filter(c => {
      if (!query) return true;
      const it = c.item;
      return [it.name, it.sku, it.supplier_name, ...(it.aliases || []), c.code, c.description]
        .filter(Boolean).some(s => s.toLowerCase().includes(query));
    })
    .sort((a, b) => a.item.name.localeCompare(b.item.name) || a.code.localeCompare(b.code));

  const onRack = rack ? containers.filter(c => c.location_id === rack) : [];

  const place = async (c, qty) => {
    try {
      if (qty != null && Math.abs(qty - c.current_qty) > 0.0001) {
        await api.post(`/containers/${c.id}/recount`, { qty, note: "Weighed during rack-up" });
      }
      if (c.location_id !== rack) {
        await api.post("/containers/move", { container_ids: [c.id], to_location_id: rack, note: "Rack-up" });
      }
      setPlaced(n => n + 1);
      toast.success(`${c.item.name} · ${c.code.split("/").pop()} → ${locById[rack]?.name}`);
      qc.invalidateQueries({ queryKey: ["containers-list"] });
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-4 animate-fade-in max-w-3xl" data-testid="arrange-page">
      <div>
        <div className="data-label">Rack-up</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Arrange</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Choose the rack you are filling, then tap each can as you put it there. Weigh it first if you can.
        </p>
      </div>

      <div className="sticky top-14 md:top-0 z-20 bg-background pb-2 space-y-2">
        <select className="field font-semibold" value={rack} onChange={(e) => setRack(e.target.value)}
                data-testid="arrange-rack">
          <option value="">Which rack are you filling?</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.code} · {locPath(l.id)}</option>)}
        </select>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="field pl-9" placeholder="Search can: item name, code, or C-number"
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="arrange-search" />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{placed} placed this session · {onRack.length} cans on this rack</span>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} />
            hide cans already here
          </label>
        </div>
      </div>

      {!rack && (
        <div className="p-6 text-sm text-muted-foreground bg-white border border-border rounded-sm">
          Pick a rack above to start. Racks are created in Setup &rarr; Godowns &amp; Racks.
        </div>
      )}

      {rack && (
        <ul className="divide-y divide-border bg-white border border-border rounded-sm" data-testid="arrange-list">
          {rows.length === 0 && <li className="p-4 text-sm text-muted-foreground">No cans match.</li>}
          {rows.map(c => (
            <CanRow key={c.id} c={c} here={c.location_id === rack} path={locPath(c.location_id)} onPlace={place} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CanRow({ c, here, path, onPlace }) {
  const [weighing, setWeighing] = useState(false);
  const [w, setW] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (qty) => { setBusy(true); await onPlace(c, qty); setBusy(false); setWeighing(false); setW(""); };
  return (
    <li className="p-3 flex items-center gap-3 flex-wrap" data-testid={`arrange-can-${c.code}`}>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{c.item.name}
          <span className="font-mono text-xs text-muted-foreground ml-2">{c.code}</span>
        </div>
        <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {path} · {c.description}
        </div>
      </div>
      <div className="font-mono font-semibold">{c.current_qty} <span className="text-xs text-muted-foreground">{c.item.base_unit}</span></div>
      {here ? (
        <span className="badge-flat bg-success/10 text-success"><Check className="w-3 h-3 inline mr-1" />here</span>
      ) : weighing ? (
        <div className="flex items-center gap-1">
          <input type="number" step="0.001" className="field-sm w-24 font-mono" placeholder={String(c.current_qty)}
                 value={w} onChange={(e) => setW(e.target.value)} autoFocus />
          <button className="btn-primary text-xs" disabled={busy} onClick={() => go(w === "" ? null : Number(w))}>Put here</button>
          <button className="btn-ghost text-xs" onClick={() => setWeighing(false)}>Cancel</button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <button className="btn-ghost text-xs" title="Weigh and correct" onClick={() => setWeighing(true)}
                  data-testid={`arrange-weigh-${c.code}`}><Scale className="w-4 h-4" /></button>
          <button className="btn-primary text-xs" disabled={busy} onClick={() => go(null)}
                  data-testid={`arrange-put-${c.code}`}>Put here</button>
        </div>
      )}
    </li>
  );
}
